import React from 'react'
import copy from 'copy-to-clipboard'
import { cloneDeep, isEqual } from 'lodash'
import { withTranslation } from 'react-i18next'
import keydown from 'react-keydown'

import VideoTips from '../../../../common/components/Tips/Tips'
import ConfirmationDialog from '../../../../common/components/Modal/ConfirmationDialog/ConfirmationDialog'
import ErrorBoundary from '../../../../common/components/ErrorBoundary/ErrorBoundary'
import Toast from '../../../../common/components/Toast/Toast'
import { genUniqueId } from '../../../../common/utils/genUniqueId'

import ModalController from './Modals/ModalController.jsx'

import { BLOCK_DICTIONARY, BLOCK_SCHEMA_DICTIONARY } from './Blocks/BlockSettings'
import BlocksController from './Blocks/BlocksController.jsx'
import Add from './Blocks/Add'

import BlocksPane from './Panels/Blocks'
import ControlPane from './Panels/Control'
import InternalPages from './Panels/InternalPages/InternalPages.jsx'

import Normalizer from './util/normalizer'
import DataSchema from './util/schema'
import Page from './util/PageModel.js'
import { filterBlocks, normalizeBlocks, getInfo, createBlockData, normalizeBlock } from './util/editorUtils.js'
import { getControls } from './controls'
import { APP_SCHEME, PROJECT_DATA_TYPES } from './constants.js'

import './index.scss'
import './Blocks/Blocks.scss'

class EditorTab extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            isReady: false,

            data: {
                app: null,
                pages: null,
            },

            selectedPage: null,
            selectedBlock: null,
            selectedBlockArrayProperty: null,

            openBlockPane: false,
            insertBeforeId: null,

            confirmationDialog: {
                isOpen: false,
                type: null, // block-remove, etc.
                payload: {},
            },

            modal: {
                name: null,
                isOpen: false,
                payload: {},
            },
        }
    }

    async componentDidMount() {
        const {
            data: {
                project: { projectStructureJson },
            },
        } = this.props

        await this.setState(prevState => ({
            data: {
                ...prevState.data,
                app: new Normalizer(new DataSchema(APP_SCHEME)).process(projectStructureJson.app),
                pages: projectStructureJson.pages,
            },
            selectedPage: projectStructureJson.pages[0],
            isReady: true,
        }))
    }

    updateStateData = async (type, _data) => {
        const {
            methods: { changeProject },
        } = this.props

        switch (type) {
            case PROJECT_DATA_TYPES.app: {
                await this.setState(prevState => ({
                    data: {
                        ...prevState.data,
                        app: _data,
                    },
                }))
                await changeProject('projectStructureJson.app', _data)
                break
            }
            case PROJECT_DATA_TYPES.pages: {
                await this.setState(prevState => ({
                    data: {
                        ...prevState.data,
                        pages: _data,
                    },
                }))
                await changeProject('projectStructureJson.pages', _data)
                break
            }
            case PROJECT_DATA_TYPES.blocks: {
                const {
                    data: { pages },
                    selectedPage,
                    selectedBlock,
                } = this.state
                const newPages = pages.map(page => (page.id === selectedPage.id ? { ...page, blocks: _data } : page))
                const newSelectPage = newPages.find(page => page.id === selectedPage.id)
                let newSelectedBlock = selectedBlock
                if (selectedBlock) {
                    newSelectedBlock = newSelectPage.blocks.find(block => block.id === selectedBlock.id)
                }
                await this.setState(prevState => ({
                    data: {
                        ...prevState.data,
                        pages: newPages,
                    },
                    selectedPage: newSelectPage,
                    selectedBlock: newSelectedBlock,
                }))
                await changeProject('projectStructureJson.pages', newPages)
                break
            }
            default: {
                break
            }
        }
    }

    @keydown(['Ctrl+e', 'Meta+e'])
    copyProjectOrBLockJson() {
        try {
            const {
                methods: { getEncodedObject },
            } = this.props
            const { selectedBlock } = this.state

            if (selectedBlock) {
                copy(JSON.stringify(getEncodedObject(cloneDeep(selectedBlock))))
                Toast('success', {
                    title: 'Block data copied to clipboard!',
                    time: 2000,
                })
            } else {
                copy(JSON.stringify(getEncodedObject()))
                Toast('success', {
                    title: 'Project data copied to clipboard!',
                    time: 2000,
                })
            }
        } catch (err) {
            console.error(err)
            Toast('error', {})
        }
    }

    @keydown(['Ctrl+i', 'Meta+i'])
    async replaceProjectOrBlockJson() {
        try {
            const {
                methods: { getDecodedObject },
            } = this.props
            const { selectedBlock, selectedPage } = this.state

            const clipboardText = await navigator.clipboard.readText()
            const parsedData = getDecodedObject(JSON.parse(clipboardText))

            if (selectedBlock && selectedPage) {
                const blockIndex = selectedPage.blocks.findIndex(b => b.id === selectedBlock.id)
                if (blockIndex !== -1) {
                    const _blocks = cloneDeep(selectedPage.blocks)
                    const normalizedBlock = normalizeBlocks([...filterBlocks([parsedData])])
                    if (normalizedBlock.length) {
                        _blocks[blockIndex] = {
                            ...normalizedBlock[0],
                            id: genUniqueId(),
                        }

                        this.updateStateData(PROJECT_DATA_TYPES.blocks, _blocks)

                        Toast('success', {
                            title: 'Block data replaced from clipboard!',
                            time: 2000,
                        })
                    }
                }
            } else {
                const dataToInsert = {}

                if (parsedData.hasOwnProperty(PROJECT_DATA_TYPES.app)) {
                    const _app = new Normalizer(new DataSchema(APP_SCHEME)).process(parsedData.app)
                    dataToInsert.app = _app
                    this.updateStateData(PROJECT_DATA_TYPES.app, _app)
                }

                if (parsedData.hasOwnProperty(PROJECT_DATA_TYPES.pages)) {
                    const _pages = parsedData.pages.map(page => {
                        const _blocks = normalizeBlocks([...filterBlocks(page.blocks)])
                        return { ...page, blocks: _blocks }
                    })
                    dataToInsert.pages = _pages
                    this.updateStateData(PROJECT_DATA_TYPES.pages, _pages)
                }

                if (Object.keys(dataToInsert).length) {
                    Toast('success', {
                        title: 'Project data replaced from clipboard!',
                        time: 2000,
                    })
                }
            }
        } catch (err) {
            console.error(err)
            Toast('error', {})
        }
    }

    onAddBlock = async (blockType, insertBeforeId = null, blockData = {}) => {
        const { selectedPage } = this.state

        const _blocks = cloneDeep(selectedPage.blocks)

        let insertIndex = _blocks.length

        if (insertBeforeId) {
            const blockIndex = _blocks.findIndex(b => b.id === insertBeforeId)
            if (blockIndex !== -1) {
                insertIndex = blockIndex
            }
        }

        const newBlock = normalizeBlock(createBlockData(blockType, blockData))

        _blocks.splice(insertIndex, 0, newBlock)

        this.updateStateData(PROJECT_DATA_TYPES.blocks, _blocks)

        this.onBlockSelect(newBlock)
        // если был добавлен реальный блок (не служебный) то закрыть панель
        this.setState({
            insertBeforeId: null,
            openBlockPane: false,
        })
    }

    onOpenBlockPane = id => {
        this.setState({
            selectedBlock: null,
            selectedBlockArrayProperty: null,
            openBlockPane: true,
            insertBeforeId: id,
        })
    }

    onInlineControlAction = async ({ action, blockId }, force = false) => {
        switch (action) {
            case 'clone': {
                const { selectedPage } = this.state

                const _blocks = cloneDeep(selectedPage.blocks)

                const blockIndex = _blocks.findIndex(el => el.id === blockId)

                if (blockIndex !== -1) {
                    const parentBlock = _blocks[blockIndex]
                    const nextBlock = _blocks[blockIndex + 1]
                    await this.onAddBlock(parentBlock.t, nextBlock ? nextBlock.id : null, parentBlock)
                }
                break
            }
            case 'delete': {
                if (force) {
                    const { selectedPage } = this.state

                    const _blocks = cloneDeep(selectedPage.blocks)

                    const blockIndex = _blocks.findIndex(el => el.id === blockId)

                    if (blockIndex !== -1) {
                        _blocks.splice(blockIndex, 1)

                        this.updateStateData(PROJECT_DATA_TYPES.blocks, _blocks)
                        this.onBlockSelect(null)
                    }
                } else {
                    this.showConfirmationDialog('block-remove', { id: blockId })
                }
                break
            }
            case 'up': {
                const { selectedPage } = this.state

                const _blocks = cloneDeep(selectedPage.blocks)

                const { index } = getInfo(_blocks, blockId)

                if (index > 0) {
                    const temp = _blocks[index - 1]
                    _blocks[index - 1] = _blocks[index]
                    _blocks[index] = temp

                    this.updateStateData(PROJECT_DATA_TYPES.blocks, _blocks)
                }
                break
            }
            case 'down': {
                const { selectedPage } = this.state

                const _blocks = cloneDeep(selectedPage.blocks)

                const { index } = getInfo(_blocks, blockId)

                if (index < _blocks.length - 1) {
                    const temp = _blocks[index + 1]
                    _blocks[index + 1] = _blocks[index]
                    _blocks[index] = temp

                    this.updateStateData(PROJECT_DATA_TYPES.blocks, _blocks)
                }
                break
            }
            default:
                break
        }
    }

    showConfirmationDialog = (type, payload) => {
        this.setState({
            confirmationDialog: {
                isOpen: true,
                type,
                payload,
            },
        })
    }

    closeConfirmationDialog = () => {
        this.setState({
            confirmationDialog: {
                isOpen: false,
                type: null,
                payload: {},
            },
        })
    }

    openModalByName = (name, payload = {}) => {
        this.setState({
            modal: {
                name,
                isOpen: true,
                payload,
            },
        })
    }

    closeModal = () => {
        this.setState({
            modal: {
                name: null,
                isOpen: false,
                payload: null,
            },
        })
    }

    onPageSelect = page => {
        const { selectedPage } = this.state
        if (selectedPage && page && selectedPage.id === page.id) {
            return
        }
        this.setState({ selectedPage: page, selectedBlock: null, selectedBlockArrayProperty: null })
    }

    onBlockSelect = block => {
        const { selectedBlock, selectedBlockArrayProperty } = this.state

        if (selectedBlock && block && selectedBlock.id === block.id) {
            if (selectedBlockArrayProperty) {
                this.setState({
                    selectedBlockArrayProperty: null,
                })
            }
            return
        }

        this.setState({
            selectedBlock: block,
            selectedBlockArrayProperty: null,
        })
    }

    onOverlayClick = () => {
        const { selectedBlock } = this.state
        if (selectedBlock) {
            this.setState({
                selectedBlock: null,
                selectedBlockArrayProperty: null,
            })
        }
    }

    changeData = async (target, payload) => {
        switch (target) {
            case PROJECT_DATA_TYPES.app: {
                const {
                    data: { app },
                } = this.state

                const _app = {
                    ...cloneDeep(app),
                    ...payload,
                }

                const normalizedBlock = new Normalizer(new DataSchema(APP_SCHEME)).process(_app)

                if (!isEqual(app, normalizedBlock)) {
                    this.updateStateData(PROJECT_DATA_TYPES.app, _app)
                }
                break
            }
            case PROJECT_DATA_TYPES.pages: {
                const {
                    data: { pages },
                } = this.state
                const _pages = payload.pages
                if (!isEqual(pages, _pages)) {
                    this.updateStateData(PROJECT_DATA_TYPES.pages, _pages)
                }
                break
            }
            case PROJECT_DATA_TYPES.blocks: {
                const { selectedPage } = this.state

                try {
                    const _blocks = cloneDeep(selectedPage.blocks)

                    for (const [id, value] of Object.entries(payload)) {
                        const blockIndex = _blocks.findIndex(b => b.id === id)
                        if (blockIndex === -1) {
                            return
                        }
                        let block = cloneDeep(_blocks[blockIndex])

                        if (value.payload && value.payload.selectedBlockArrayProperty) {
                            // это запрос на редактирование элемента дочернего массива блока
                            const { arrName, index, schema } = value.payload.selectedBlockArrayProperty

                            block[arrName][index] = new Normalizer(new DataSchema(schema)).process({
                                ...block[arrName][index],
                                ...value.data,
                            })
                        } else {
                            block = { ...block, ...value.data }
                        }

                        const normalizedBlock = new Normalizer(
                            new DataSchema(BLOCK_SCHEMA_DICTIONARY[block.t]),
                        ).process(block)

                        if (!isEqual(_blocks[blockIndex], normalizedBlock)) {
                            _blocks[blockIndex] = normalizedBlock

                            this.updateStateData(PROJECT_DATA_TYPES.blocks, _blocks)
                        }
                    }
                } catch (err) {
                    console.error(err)
                }
                break
            }
            default:
                break
        }
    }

    /**
     * Запросить контролы для элемента в массиве
     * В блоке может быть массив, например массив точек в зум-карте.
     * Это возможность для каждой точки показать контролы при клике на нее
     *
     * @param {*} id идентификатор блока
     * @param {*} arrName имя свойства
     * @param {*} index
     */
    requestArrayElementControls = (id, arrName, index) => {
        const { selectedPage } = this.state

        const block = selectedPage.blocks.find(b => b.id === id),
            schema = BLOCK_SCHEMA_DICTIONARY[`${block.t}.${arrName}`]

        if (schema) {
            this.setState({
                selectedBlock: block,
                selectedBlockArrayProperty: { arrName, index, schema },
            })
        } else {
            this.setState({
                selectedBlock: null,
                selectedBlockArrayProperty: null,
            })
            console.error(`Schema not found for ${block.t}.${arrName}`)
        }
    }

    getAppControls = () => {
        return getControls({
            target: PROJECT_DATA_TYPES.app,
        })
    }

    getBlockControls = () => {
        const { selectedBlock, selectedBlockArrayProperty } = this.state
        let key = selectedBlock.t
        if (selectedBlock.t && selectedBlockArrayProperty) {
            key = `${selectedBlock.t}.${selectedBlockArrayProperty.arrName}`
        }
        return getControls({
            target: PROJECT_DATA_TYPES.blocks,
            typeIdentifier: key,
            blockData: selectedBlock,
        })
    }

    render() {
        const { methods, i18n } = this.props
        const {
            isReady,
            data,
            selectedPage,
            selectedBlock,
            selectedBlockArrayProperty,
            openBlockPane,
            insertBeforeId,
            confirmationDialog,
            modal,
        } = this.state

        return (
            <>
                {isReady && (
                    <div className="artboard">
                        <div className="overlay" onClick={this.onOverlayClick} />
                        {selectedPage && (
                            <>
                                <div
                                    className="block_page"
                                    style={{ backgroundColor: data.app.bg }}
                                    onClick={this.onOverlayClick}
                                >
                                    {selectedPage.blocks.map((block, index) => {
                                        return (
                                            <ErrorBoundary key={block.id}>
                                                <BlocksController
                                                    block={block}
                                                    appData={data.app}
                                                    isSelected={selectedBlock && selectedBlock.id === block.id}
                                                    isLast={index === selectedPage.blocks.length - 1}
                                                    isFirst={index === 0}
                                                    methods={{
                                                        openAssetManager: methods.openAssetManager,
                                                        onBlockSelect: this.onBlockSelect,
                                                        changeData: this.changeData,
                                                        requestArrayElementControls: this.requestArrayElementControls,
                                                        onOpenBlockPane: this.onOpenBlockPane,
                                                        onInlineControlAction: this.onInlineControlAction,
                                                    }}
                                                />
                                            </ErrorBoundary>
                                        )
                                    })}
                                    {/* "Quick add" panel */}
                                    <div
                                        className={`block-control __static ${
                                            !selectedPage.blocks.length ? 'is-alone' : ''
                                        }`}
                                    >
                                        <Add
                                            onAdd={this.onAddBlock}
                                            onOpenBlockPane={this.onOpenBlockPane}
                                            openAssetManager={methods.openAssetManager}
                                        />
                                        {!selectedPage.blocks.length && (
                                            <div className="text">{i18n.t('Start creating your article')}</div>
                                        )}
                                    </div>
                                </div>
                                <VideoTips blocks={selectedPage.blocks} />
                            </>
                        )}

                        <InternalPages
                            pages={data.pages}
                            selectedPage={selectedPage}
                            onUpdate={_data => this.changeData(PROJECT_DATA_TYPES.pages, _data)}
                            onSelectPage={this.onPageSelect}
                        />

                        <BlocksPane
                            openAssetManager={methods.openAssetManager}
                            open={openBlockPane}
                            onAdd={this.onAddBlock}
                            getDecodedObject={methods.getDecodedObject}
                            insertBeforeId={insertBeforeId}
                            onClose={() => this.setState({ openBlockPane: false })}
                        />

                        {selectedBlock ? (
                            <ControlPane
                                methods={{
                                    openAssetManager: methods.openAssetManager,
                                    openModalByName: this.openModalByName,
                                }}
                                blockName={BLOCK_DICTIONARY[selectedBlock.t].labelForRightPanel}
                                schema={BLOCK_SCHEMA_DICTIONARY[selectedBlock.t]}
                                controls={this.getBlockControls()}
                                blockData={selectedBlock}
                                selectedBlockArrayProperty={selectedBlockArrayProperty}
                                onUpdate={_data => this.changeData(PROJECT_DATA_TYPES.blocks, _data)}
                                isApp={false}
                            />
                        ) : (
                            <ControlPane
                                methods={{
                                    openAssetManager: methods.openAssetManager,
                                    openModalByName: this.openModalByName,
                                }}
                                blockName={i18n.t('Project settings')}
                                schema={APP_SCHEME}
                                controls={this.getAppControls()}
                                blockData={data.app}
                                onUpdate={_data => this.changeData(PROJECT_DATA_TYPES.app, _data)}
                                isApp={true}
                            />
                        )}
                    </div>
                )}
                {confirmationDialog.isOpen && confirmationDialog.type === 'block-remove' ? (
                    <ConfirmationDialog
                        onClose={() => this.closeConfirmationDialog()}
                        onAction={() =>
                            this.onInlineControlAction(
                                {
                                    action: 'delete',
                                    blockId: confirmationDialog.payload.id,
                                },
                                true,
                            ) && this.closeConfirmationDialog()
                        }
                        data={{
                            headText: i18n.t('Are you sure you want to delete this block?'),
                            noteText: i18n.t('The content of the block will also be deleted.'),
                            actionVariant: 'primary-danger',
                            actionText: i18n.t('Delete'),
                            cancelText: i18n.t('Cancel'),
                        }}
                    />
                ) : null}
                <ModalController
                    modal={modal}
                    data={data}
                    methods={{
                        openAssetManager: methods.openAssetManager,
                        closeModal: this.closeModal,
                        changeData: this.changeData,
                    }}
                />
            </>
        )
    }
}

export default withTranslation('translations')(EditorTab)
